/**
 * How many requests a page should have in flight at once when it fetches
 * records one by one. Every API request looks the user up in Cognito, and a
 * burst of hundreds trips its rate limit ("Too many requests").
 */
export const DEFAULT_REQUEST_CONCURRENCY = 6;

export type ConcurrencyLimiter = <T>(task: () => Promise<T>) => Promise<T>;

/**
 * Returns a function that runs tasks with at most `limit` running at once.
 * Tasks beyond the limit wait in a queue and start as earlier ones finish.
 * Share one limiter between several lists to cap them together.
 */
export const createConcurrencyLimiter = (
  limit: number = DEFAULT_REQUEST_CONCURRENCY,
): ConcurrencyLimiter => {
  const maxRunning = Math.max(1, Math.floor(limit));
  const queue: Array<() => void> = [];
  let running = 0;

  const startNext = () => {
    if (running >= maxRunning) return;
    const next = queue.shift();
    if (!next) return;
    running += 1;
    next();
  };

  return <T>(task: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      queue.push(() => {
        // Promise.resolve().then so a task that throws synchronously still
        // frees its slot.
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            running -= 1;
            startNext();
          });
      });
      startNext();
    });
};

/**
 * Like `Promise.all(items.map(fn))`, but runs at most `limit` calls at once.
 * Results keep the order of `items`.
 */
export const mapWithConcurrency = <T, R>(
  items: readonly T[],
  fn: (item: T, index: number) => Promise<R>,
  limiter: ConcurrencyLimiter = createConcurrencyLimiter(),
): Promise<R[]> =>
  Promise.all(items.map((item, index) => limiter(() => fn(item, index))));
